// Derived issue summary logic for the live dashboard page.
export const dashboardLiveIssueSummaryComputed = {
    /**
     * Critical issue items.
     */
    criticalIssueItems() {
        return this.getCriticalIssueSummaryItems();
    },
    /**
     * Critical issue count.
     */
    criticalIssueCount() {
        return this.getIssueSummaryItemCount(this.criticalIssueItems);
    },
    /**
     * Check whether critical issues.
     */
    hasCriticalIssues() {
        return this.criticalIssueCount > 0;
    },
};

const dashboardLiveIssueSummary = {
    /**
     * Check whether one widget has non-empty list content.
     *
     * @param {*} widget
     */
    hasWidgetListContent(widget) {
        const attributes = this.getWidgetAttributes(widget);

        return Array.isArray(attributes.items) && attributes.items.length > 0;
    },
    /**
     * Get widget status count.
     *
     * @param {*} attributes
     * @param {*} fieldName
     */
    getWidgetStatusCount(attributes, fieldName) {
        const value = Number(attributes[fieldName]);

        if (!Number.isFinite(value)) {
            return null;
        }

        return Math.max(value, 0);
    },
    /**
     * Get the total issue count for one dashboard widget.
     *
     * @param {*} widget
     */
    getWidgetIssueCount(widget) {
        const attributes = this.getWidgetAttributes(widget);
        const issueCount = this.getWidgetStatusCount(attributes, 'issueCount');

        if (issueCount !== null) {
            return issueCount;
        }

        return attributes.statusLabel && attributes.isEnabled
            ? 1
            : 0;
    },
    /**
     * Get the critical issue count for one dashboard widget.
     *
     * @param {*} widget
     */
    getWidgetCriticalIssueCount(widget) {
        const attributes = this.getWidgetAttributes(widget);
        const criticalIssueCount = this.getWidgetStatusCount(attributes, 'criticalIssueCount');

        if (criticalIssueCount !== null) {
            return criticalIssueCount;
        }

        return 0;
    },
    /**
     * Create a normalized issue summary item for one widget.
     *
     * @param {*} widget
     * @param {*} issueCount
     */
    createIssueSummaryItem(widget, issueCount) {
        const attributes = this.getWidgetAttributes(widget);
        const criticalIssueCount = this.getWidgetCriticalIssueCount(widget);

        return {
            count: issueCount,
            critical: criticalIssueCount > 0,
            description: attributes.statusDescription || '',
            href: attributes.href || '',
            target: attributes.target || '_self',
            title: attributes.statusLabel || attributes.title || widget?.type || '',
            value: attributes.statusValue || '',
        };
    },
    /**
     * Sort dashboard issue summaries by importance.
     *
     * @param {*} items
     */
    sortIssueSummaryItems(items) {
        const sortedItems = [...items];

        /*
         * Keep critical items first so the issue summary reflects the same
         * priority as the critical alert strip above the dashboard.
         */
        sortedItems.sort((leftItem, rightItem) => {
            if (leftItem.critical === rightItem.critical) {
                return 0;
            }

            return leftItem.critical ? -1 : 1;
        });

        return sortedItems;
    },
    /**
     * Get issue summary item count.
     *
     * @param {*} items
     */
    getIssueSummaryItemCount(items) {
        let count = 0;

        for (const item of items) {
            count += item.count;
        }

        return count;
    },
    /**
     * Get tab issue count.
     *
     * @param {*} tabId
     */
    getTabIssueCount(tabId) {
        let count = 0;
        const normalizedTabId = String(tabId || '').trim();
        const tabWidgets = this.getWidgetsForTab(tabId);

        for (const widget of tabWidgets) {
            count += this.getWidgetIssueCount(widget);
        }

        /*
         * Content & activiteit widgets are mostly informational. When there is
         * activity (list items), show at least one tab badge so it does not
         * look like a static all-green tab.
         */
        if (normalizedTabId === 'content-activity' && count === 0) {
            const hasContentActivity = tabWidgets.some((widget) => this.hasWidgetListContent(widget));

            if (hasContentActivity) {
                return 1;
            }
        }

        return count;
    },
    /**
     * Get issue summary items for the active dashboard tab.
     */
    getIssueSummaryItems() {
        const items = [];

        /*
         * The issue summary widget is derived from the rest of the dashboard,
         * so keep the collection logic in one place instead of duplicating it
         * inside individual widgets.
         */
        for (const widget of this.allDashboardWidgets) {
            const issueCount = this.getWidgetIssueCount(widget);

            if (!issueCount) {
                continue;
            }

            items.push(this.createIssueSummaryItem(widget, issueCount));
        }

        return this.sortIssueSummaryItems(items);
    },
    /**
     * Get critical issue summary items for the dashboard alert area.
     */
    getCriticalIssueSummaryItems() {
        const items = [];

        /*
         * The critical alert strip only shows issues that have an explicit
         * critical counter. Regular warning counts stay inside the issue summary.
         */
        for (const widget of this.allDashboardWidgets) {
            const issueCount = this.getWidgetCriticalIssueCount(widget);

            if (!issueCount) {
                continue;
            }

            items.push(this.createIssueSummaryItem(widget, issueCount));
        }

        return items;
    },
};

export default dashboardLiveIssueSummary;
